import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { createRole, updateRole } from "../utils/roleApi";
import { toast } from "react-toastify";
import { PermissionSelectorMUI } from "./PermissionSelectorMUI";
import { MenuPermission } from "../utils/menuPermissions";

interface Role {
  _id?: string;
  name: string;
  displayText: string;
  isActive: boolean;
  permissions?: MenuPermission[];
}

interface RoleFormMUIProps {
  role?: Role | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const RoleFormMUI: React.FC<RoleFormMUIProps> = ({
  role,
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<Role>({
    name: "",
    displayText: "",
    isActive: true,
    permissions: [],
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        displayText: role.displayText,
        isActive: role.isActive,
        permissions: role.permissions || [],
      });
    } else {
      // Reset form when opening for new role
      setFormData({
        name: "",
        displayText: "",
        isActive: true,
        permissions: [],
      });
      setErrors({});
    }
  }, [role, open]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Rollenname ist erforderlich";
    }

    if (!formData.displayText.trim()) {
      newErrors.displayText = "Rollenbeschreibung ist erforderlich";
    }

    if (!formData.permissions || formData.permissions.length === 0) {
      newErrors.permissions = "Mindestens eine Berechtigung muss ausgewählt werden";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (role?._id) {
        // Update existing role
        const response = await updateRole({
          id: role._id,
          roleName: formData.name,
          roleDescription: formData.displayText,
          isActive: formData.isActive,
          permissions: formData.permissions,
        });

        if (response.data.success) {
          toast.success("Rolle erfolgreich aktualisiert");
          onSuccess();
          onClose();
        } else {
          const errorMessage = response.data.message || "Ein Fehler ist aufgetreten";
          toast.error(errorMessage);
        }
      } else {
        // Create new role
        const response = await createRole({
          roleName: formData.name,
          displayText: formData.displayText,
          isActive: formData.isActive,
          permissions: formData.permissions,
        });

        if (response.data.success) {
          toast.success("Rolle erfolgreich erstellt");
          onSuccess();
          onClose();
        } else {
          const errorMessage = response.data.message || "Ein Fehler ist aufgetreten";
          toast.error(errorMessage);
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Ein Fehler ist aufgetreten";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    const fieldValue = e.target.type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlePermissionsChange = (permissions: MenuPermission[]) => {
    setFormData((prev) => ({
      ...prev,
      permissions,
    }));

    // Clear permissions error
    if (errors.permissions) {
      setErrors((prev) => ({
        ...prev,
        permissions: "",
      }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          maxWidth: "1200px",
          width: "90%",
          maxHeight: "90vh",
        },
      }}
      aria-labelledby="role-form-dialog-title"
    >
      <DialogTitle id="role-form-dialog-title" sx={{ pr: 6 }}>
        <Typography variant="h6">
          {role?._id ? "Rolle bearbeiten" : "Neue Rolle erstellen"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 1,
            top: 1,
            color: "grey.500",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }} dividers>
          <TextField
            fullWidth
            label="Rollenname"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name || "z.B. Admin, Benutzer, Moderator"}
            disabled={loading}
            required
            sx={{ mb: 3 }}
            variant="outlined"
          />

          <TextField
            fullWidth
            label="Beschreibung"
            name="displayText"
            value={formData.displayText}
            onChange={handleChange}
            error={!!errors.displayText}
            helperText={
              errors.displayText || "Beschreibung der Rolle und ihrer Berechtigungen"
            }
            disabled={loading}
            required
            multiline
            rows={3}
            sx={{ mb: 3 }}
            variant="outlined"
          />

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange}
                  name="isActive"
                  disabled={loading}
                  color="success"
                />
              }
              label="Rolle ist aktiv"
            />
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                fontSize: "0.875rem",
                color: "text.secondary",
              }}
            >
              Inaktive Rollen können nicht zugewiesen werden
            </Typography>
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              Berechtigungen *
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Wählen Sie die Menüpunkte aus, auf die diese Rolle zugreifen kann, und
              legen Sie die CRUD-Berechtigungen fest.
            </Typography>
            {errors.permissions && (
              <Typography variant="caption" color="error">
                {errors.permissions}
              </Typography>
            )}
          </Box>

          <PermissionSelectorMUI
            permissions={formData.permissions || []}
            onChange={handlePermissionsChange}
            disabled={loading}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading} color="inherit">
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            color="primary"
          >
            {loading ? "Wird gespeichert..." : role?._id ? "Aktualisieren" : "Erstellen"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
